/**
 * TechnicianBottomNav Component
 *
 * Fixed bottom navigation bar for the Technician Portal.
 * Contains 5 navigation items with icons and labels.
 * Touch-friendly with minimum 44px height targets.
 *
 * @module components/technician/layout/TechnicianBottomNav
 */

import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiHome, FiFolder, FiCheckSquare, FiCamera, FiPackage, FiMenu } from 'react-icons/fi';
import PropTypes from 'prop-types';
import './TechnicianLayout.css';

const navItems = [
  {
    path: '/tecnico/dashboard',
    icon: FiHome,
    label: 'Dashboard',
    exact: true
  },
  {
    path: '/tecnico/proyectos',
    icon: FiFolder,
    label: 'Proyectos',
    exact: false
  },
  {
    path: '/tecnico/tareas',
    icon: FiCheckSquare,
    label: 'Tareas',
    exact: false
  },
  {
    path: '/tecnico/progreso',
    icon: FiCamera,
    label: 'Progreso',
    exact: false
  },
  {
    path: '/tecnico/materiales',
    icon: FiPackage,
    label: 'Materiales',
    exact: false
  }
];

const TechnicianBottomNav = ({ onMoreClick, moreMenuOpen = false }) => {
  const location = useLocation();

  // Check if a path is active (handles nested routes)
  const isPathActive = (path, exact) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Check if any "More" menu item is active
  const isMoreActive = () => {
    const morePaths = [
      '/agente',
      '/mis-herramientas',
      '/bodega',
      '/tecnico/horas',
      '/tecnico/incidentes',
      '/tecnico/mensajes',
      '/tecnico/perfil'
    ];
    return morePaths.some(path => location.pathname.startsWith(path));
  };

  return (
    <nav className="technician-bottom-nav" role="navigation" aria-label="Navegacion principal">
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = isPathActive(item.path, item.exact);

        return (
          <NavLink
            key={item.path}
            to={item.path}
            className={`bottom-nav-item ${isActive ? 'active' : ''}`}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className="bottom-nav-icon">
              <Icon size={24} />
            </span>
            <span className="bottom-nav-label">{item.label}</span>
          </NavLink>
        );
      })}

      {/* More Menu Button */}
      <button
        className={`bottom-nav-item more-btn ${moreMenuOpen || isMoreActive() ? 'active' : ''}`}
        onClick={onMoreClick}
        aria-expanded={moreMenuOpen}
        aria-haspopup="true"
        aria-label="Ver mas opciones"
      >
        <span className="bottom-nav-icon">
          <FiMenu size={24} />
        </span>
        <span className="bottom-nav-label">Mas</span>
      </button>
    </nav>
  );
};

TechnicianBottomNav.propTypes = {
  onMoreClick: PropTypes.func.isRequired,
  moreMenuOpen: PropTypes.bool
};

export default TechnicianBottomNav;
